import { SelectChangeEvent, SxProps } from '@mui/material';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from 'components';
import { useWindowSize } from 'hooks';
import { Comment, InViewItemRef } from 'types';

import { CommentListItem } from '../CommentListItem';
import { CommentsLabelW, ListW, SortBarW, SortMenuItem, SortSelect } from './styled';

type SortOrder = 'newest' | 'oldest';

interface CommentsListProps {
  postId?: string;
  postAuthor: string;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  comments: Comment[];
  lastItemRef?: InViewItemRef;
  listWrapSx?: SxProps;
  inModal?: boolean;
}

export function CommentsList({
  comments,
  postAuthor,
  isLoading,
  lastItemRef,
  isFetchingNextPage,
  listWrapSx,
  inModal,
}: CommentsListProps): ReactElement {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');

  if (isLoading) {
    return <Spinner />;
  }

  const sorted = [...comments].sort((a, b) => {
    const diff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    return sortOrder === 'newest' ? -diff : diff;
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <CommentsLabelW>{t('post.details.commentsLabel')}</CommentsLabelW>
        {!(isMobile && !inModal) && (
          <SortBarW>
            <SortSelect
              value={sortOrder}
              onChange={(e: SelectChangeEvent<unknown>) =>
                setSortOrder(e.target.value as SortOrder)
              }
              displayEmpty
              renderValue={() => t('comments.sortBy')}
            >
              <SortMenuItem value="newest">{t('comments.newestFirst')}</SortMenuItem>
              <SortMenuItem value="oldest">{t('comments.oldestFirst')}</SortMenuItem>
            </SortSelect>
          </SortBarW>
        )}
      </div>

      <ListW sx={listWrapSx}>
        {sorted.map((comment, idx) => (
          <CommentListItem
            key={comment.id}
            comment={comment}
            postAuthor={postAuthor}
            lastItemRef={idx === sorted.length - 1 ? lastItemRef : undefined}
            inModal={inModal}
          />
        ))}

        {isFetchingNextPage && <Spinner wrapperSx={{ bottom: '0', top: 'unset' }} />}
      </ListW>
    </>
  );
}
